import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Target, Award, TrendingUp } from "lucide-react";

/**
 * Sección de consistencia y rachas para el jugador.
 * Vinculada a sesiones REALES completadas — sin gamificación infantil.
 */
export default function ConsistencyPanel({ endpoint = "/me/consistency" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(endpoint).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [endpoint]);

  if (err) return null;
  if (!data) return null;

  const {
    weekly_goal, this_week_completed, this_week_goal_met,
    adherence_pct_4w, current_streak_weeks, best_streak_weeks,
    weeks_history, milestones,
  } = data;

  const weekProgressPct = Math.min(100, Math.round((this_week_completed / weekly_goal) * 100));

  return (
    <div style={{ marginBottom: 24 }} data-testid="consistency-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, color: "#a3a3a3", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}>
        <TrendingUp size={14}/> Tu constancia
      </div>

      {/* Esta semana */}
      <div className="qv-card-anim" style={{ background: "#0a0a0a", border: `1px solid ${this_week_goal_met ? "rgba(74,222,128,0.35)" : "rgba(255,255,255,0.08)"}`, padding: 18, borderRadius: 4, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Target size={16} color={this_week_goal_met ? "#4ade80" : "#a3a3a3"} />
            <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", fontWeight: 600 }}>Esta semana</span>
          </div>
          <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 32, lineHeight: 1, color: this_week_goal_met ? "#4ade80" : "#e5e5e5" }}>
            {this_week_completed}<span style={{ fontSize: 14, color: "#a3a3a3" }}>/{weekly_goal}</span>
          </div>
        </div>
        <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${weekProgressPct}%`, background: this_week_goal_met ? "linear-gradient(90deg, #4ade80, #22c55e)" : "linear-gradient(90deg, #dc2626, #b91c1c)", borderRadius: 999, transition: "width .5s ease" }} data-testid="consistency-week-bar" />
        </div>
        <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 8 }}>
          {this_week_goal_met ? "Semana cumplida. Sigue así." : `${weekly_goal - this_week_completed} sesión${weekly_goal - this_week_completed === 1 ? "" : "es"} más para cumplir tu objetivo semanal.`}
        </div>
      </div>

      {/* Racha + adherencia */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
        <StatCard icon={Flame} color={current_streak_weeks >= 4 ? "#fb923c" : "#a3a3a3"} label="Racha actual" value={current_streak_weeks} unit={current_streak_weeks === 1 ? "sem" : "sems"} testid="consistency-current-streak" />
        <StatCard icon={Award} color={best_streak_weeks >= 4 ? "#facc15" : "#a3a3a3"} label="Mejor racha" value={best_streak_weeks} unit={best_streak_weeks === 1 ? "sem" : "sems"} testid="consistency-best-streak" />
        <StatCard icon={Target} color="#e5e5e5" label="Adherencia 4 sem" value={`${adherence_pct_4w}`} unit="%" testid="consistency-adherence" />
      </div>

      {/* Histórico 12 semanas */}
      {weeks_history && weeks_history.length > 0 && (
        <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 14, borderRadius: 4, marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: "#a3a3a3", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>Últimas 12 semanas</div>
          <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 44 }}>
            {[...weeks_history].reverse().map((w) => (
              <div key={w.week_start} title={`Semana ${w.week_start} · ${w.sessions} sesiones`}
                   style={{ flex: 1, background: w.goal_met ? "#4ade80" : (w.sessions > 0 ? "#dc2626" : "rgba(255,255,255,0.08)"),
                            height: `${Math.max(15, Math.min(100, (w.sessions / weekly_goal) * 100))}%`, minHeight: 6, borderRadius: 2 }} />
            ))}
          </div>
        </div>
      )}

      {/* Hitos */}
      {milestones && milestones.some((m) => m.unlocked) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {milestones.map((m) => (
            <span key={m.weeks}
                  style={{ padding: "4px 10px", fontSize: 11, letterSpacing: "0.08em", borderRadius: 999,
                           background: m.unlocked ? "rgba(250,204,21,0.12)" : "rgba(255,255,255,0.04)",
                           color: m.unlocked ? "#facc15" : "#525252",
                           border: `1px solid ${m.unlocked ? "rgba(250,204,21,0.3)" : "rgba(255,255,255,0.06)"}` }}
                  data-testid={`milestone-${m.weeks}`}>
              {m.unlocked ? "★" : "○"} {m.weeks} semanas consistentes
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Ic, color, label, value, unit, testid }) {
  return (
    <div className="qv-card-anim" style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 14, borderRadius: 4 }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Ic size={13} color={color} />
        <span style={{ fontSize: 10, color: "#a3a3a3", letterSpacing: "0.12em", textTransform: "uppercase" }}>{label}</span>
      </div>
      <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 28, color, lineHeight: 1 }}>
        {value}<span style={{ fontSize: 11, color: "#a3a3a3", marginLeft: 3 }}>{unit}</span>
      </div>
    </div>
  );
}
